import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { schoolMe } from "./api";
import { card, h2, Bar, LICENSE_APPS } from "./ui";

const date = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const when = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export function useSchoolMe(token) {
  const [me, setMe] = useState(null);
  const reload = useCallback(() => schoolMe(token).then(setMe).catch(() => {}), [token]);
  useEffect(() => { reload(); }, [reload]);
  return [me, reload];
}

// True when this school may add one more person right now (used to enable/disable the add buttons; the server decides for real).
export const canInvite = (me) => !me || (me.setup_done !== false && (!me.license || me.license.unlimited || (me.license.active && me.license.remaining > 0)));
export const inviteBlockedReason = (me) => {
  if (me && me.setup_done === false) return me.role === "teacher" ? "Your principal has to finish setting up the school first." : "Save your school's details first: Update your school, in the left menu.";
  const l = me && me.license;
  if (!l || l.unlimited || canInvite(me)) return "";
  if (l.approval === "pending") return "Available once your demo request is approved and licences are assigned.";
  if (l.approval === "rejected") return "Your demo request wasn't approved.";
  if (l.status === "approved_no_license") return "Waiting for HikmahVerses to assign your licences.";
  if (l.status === "revoked") return "Your licence has been withdrawn.";
  if (l.status === "expired") return `Your licence ended on ${date(l.valid_until)}.`;
  return "All your licences are in use. Remove someone or ask for more.";
};

// Where the school stands: waiting for the demo / approval, or how many licences are used.
export function LicensePanel({ me, compact = false }) {
  if (!me || me.role !== "principal" || !me.license || me.license.unlimited) return null;
  const l = me.license, demo = me.demo_request;
  const box = (tone, children, status) => (
    <div className={`${card} ${tone} text-sm space-y-2`} data-testid="license-panel"><span data-testid={`license-state-${status}`} className="sr-only">{status}</span>{children}</div>
  );
  if (l.approval === "pending") {
    return box("bg-gold-500/5", (
      <>
        <div className="font-semibold text-forest-700">Your demo request is being reviewed</div>
        <p>Once it&apos;s approved and licences are assigned, you can invite your teachers and students here.</p>
        {demo && demo.booking
          ? <p data-testid="demo-booked-line">Your demo is booked for <strong>{when(demo.booking.start)}</strong>. <Link className="underline text-forest-700" to="/book-demo">Change time</Link></p>
          : <p><Link className="btn-primary inline-flex" to="/book-demo" data-testid="choose-demo-time">Choose a time for your walkthrough</Link></p>}
      </>), "pending");
  }
  if (l.approval === "rejected") {
    return box("border-red-300 bg-red-50/50", (
      <>
        <div className="font-semibold text-red-800">Your demo request wasn&apos;t approved</div>
        {demo && demo.review_note && <p>{demo.review_note}</p>}
        <p>If you think this is a mistake, reply to the email we sent you and we&apos;ll take another look.</p>
      </>), "rejected");
  }
  if (l.status === "approved_no_license") {
    return box("bg-forest-600/5", (
      <>
        <div className="font-semibold text-forest-700">Your school is approved</div>
        <p>We&apos;re assigning your licences now, and we&apos;ll email you as soon as they&apos;re ready. Then you can invite your teachers and students.</p>
        {demo && demo.booking && <p>Your demo: <strong>{when(demo.booking.start)}</strong>.</p>}
      </>), "approved_no_license");
  }
  if (l.status === "revoked" || l.status === "expired") {
    return box("border-red-300 bg-red-50/50", (
      <>
        <div className="font-semibold text-red-800">{l.status === "revoked" ? "Your licence has been withdrawn" : `Your licence ended on ${date(l.valid_until)}`}</div>
        <p>Your teachers and students can no longer use the paid apps, and new people can&apos;t be invited. Please contact HikmahVerses{l.status === "expired" ? " to renew" : ""}.</p>
      </>), l.status);
  }
  const pct = l.seats ? Math.min(100, Math.round((l.used / l.seats) * 100)) : 100;
  return box("", (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className={h2}>Licences{l.kind === "pilot" ? " · pilot" : ""}</div>
        <div className="text-foreground/70">Valid until <strong>{date(l.valid_until)}</strong></div>
      </div>
      <div className="flex items-baseline gap-2"><span className="text-2xl font-display font-semibold text-forest-700" data-testid="license-used">{l.used}</span>
        <span className="text-foreground/70">of <span data-testid="license-seats">{l.seats}</span> used · <span data-testid="license-remaining">{l.remaining}</span> left</span></div>
      <Bar percent={pct} />
      <p className="text-foreground/70" data-testid="license-apps">Apps included: <strong>{l.license && l.license.apps && l.license.apps.length
        ? LICENSE_APPS.filter(([id]) => l.license.apps.includes(id)).map(([, n]) => n).join(", ") : "all apps"}</strong></p>
      {l.over_allocated && <p className="text-amber-800" data-testid="license-over">You have more people than licences. No one is removed, but you can&apos;t add more until some are removed or licences are increased.</p>}
      {!l.over_allocated && l.remaining === 0 && <p className="text-amber-800" data-testid="license-full">All licences are in use. Remove someone to invite another person, or ask HikmahVerses for more.</p>}
      {!compact && <p className="text-foreground/60">Each teacher or student uses one licence; removing someone frees theirs. Need more? Email <a className="underline" href="mailto:school@hikmahverses.com">school@hikmahverses.com</a>.</p>}
    </>), "active");
}

export function LicensePage({ token }) {
  const [me] = useSchoolMe(token);
  if (!me) return <div className="text-forest-700">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="license-page">
      <h1 className="font-display font-semibold text-2xl text-forest-700">Licence</h1>
      {me.license && me.license.unlimited
        ? <div className={`${card} text-sm`}>Your school has no licence limit.</div>
        : <LicensePanel me={me} />}
    </div>
  );
}
